import { motion } from 'framer-motion';
import { signOut, getAuth } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import { useState } from 'react';
import logo from '../../assets/fav.png';
import { Menu, X } from 'lucide-react';

const Header = ({ onDownload }) => {
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const auth = getAuth();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header className="bg-white shadow-sm">
      {/* Desktop & Mobile Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo and Title - Always Visible */}
          <div className="flex items-center space-x-4">
            <motion.img
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              src={logo}
              alt="Logo"
              className="h-12 w-12 sm:h-16 sm:w-16"
            />
            <div className="hidden sm:block">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Student Registration System</h1>
              <p className="text-sm text-gray-600">Admin Dashboard</p>
            </div>
            {/* Mobile Title */}
            <h1 className="sm:hidden text-lg font-bold text-gray-800">SRS Admin</h1>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden sm:flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="flex flex-col items-end">
                <span className="text-sm font-medium text-gray-900">
                  Welcome, {user?.displayName || user?.email || 'Admin'}
                </span>
                <span className="text-xs text-gray-500">{user?.email}</span>
              </div>
              
              <button
                onClick={onDownload}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export PDF
              </button>
              
              <button
                onClick={handleSignOut}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="sm:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Tablet/Mobile Menu */}
<motion.div
  initial={{ height: 0, opacity: 0 }}
  animate={{
    height: isMenuOpen ? 'auto' : 0,
    opacity: isMenuOpen ? 1 : 0
  }}
  transition={{ duration: 0.2 }}
  className="lg:hidden overflow-hidden bg-white border-t border-gray-100 shadow-lg"
>
  <div className="container mx-auto">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      {/* User Info Section */}
      <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
        <div className="flex items-center space-x-4">
          <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 text-lg font-semibold">
              {user?.displayName?.[0] || user?.email?.[0] || 'A'}
            </span>
          </div>
          <div>
            <div className="text-base font-medium text-gray-900">
              Welcome, {user?.displayName || user?.email || 'Admin'}
            </div>
            <div className="text-sm text-gray-500">{user?.email}</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="space-y-2 md:pr-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Quick Actions
        </h3>
        <button
          onClick={onDownload}
          className="w-full p-3 text-left text-base font-medium text-gray-900 bg-gray-50 hover:bg-gray-100 
                   flex items-center rounded-lg transition-colors duration-150"
        >
          <svg 
            className="w-5 h-5 mr-3 text-gray-600" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" 
            />
          </svg>
          Export PDF
        </button>
      </div>

      {/* Account Actions */}
      <div className="space-y-2 md:pl-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Account
        </h3>
        <button
          onClick={handleSignOut}
          className="w-full p-3 text-left text-base font-medium text-red-600 bg-red-50 hover:bg-red-100 
                   flex items-center rounded-lg transition-colors duration-150"
        >
          <svg 
            className="w-5 h-5 mr-3" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
            />
          </svg>
          Sign Out
        </button>
      </div>
    </div>
  </div>
</motion.div>
    </header>
  );
};

export default Header;
